import { Product } from "@biz/definitions/Product";
import { INotesAndFiles } from "@biz/definitions/ProductDefinitions";
import { IProjectPackage } from "@biz/definitions/ProjectPackage";
import { getCustomFilesSafe } from "@biz/definitions/SunSailDefinitions.util";
import { EditorTextHelper } from "@biz/draftEditor/EditorTextHelper";
import { Poles } from "@biz/items/sunSail/logic/Poles";
import { SunSailLogic } from "@biz/items/sunSail/logic/SunSailLogic";
import { ProductData } from "@biz/projectCalculation/ProjectDefinitions";

export interface INotesAndFilesDescriptor {
  type: "main" | "specialConsole";
  productId: string | "shoppingCart";
  poleIndex?: number;
  productIndex?: number;
}

export interface INotesAndFilesItem {
  descriptor: INotesAndFilesDescriptor;
  content: INotesAndFiles;
}

export interface IFileMiniInfo {
  name: string;
  size: number;
}
export interface INotesAndFilesExtendedItem extends INotesAndFilesItem {
  fileInfos: Record<string, IFileMiniInfo>;
}

export type FileInfoCallback = (id: string) => Promise<IFileMiniInfo | undefined>;

export class NotesAndFilesHelper {
  static #convertNotesAndFiles(item: INotesAndFiles | undefined): INotesAndFiles | undefined {
    const notes = item?.notes; // OrderFormManager.getNotesHTML(item?.notes);
    const files = getCustomFilesSafe(item);
    if (EditorTextHelper.isEmpty(notes) && files.length === 0)
      return undefined;
    return { notes: EditorTextHelper.isEmpty(notes) ? undefined : notes, files }
  }

  static getNotesAndFilesFromProduct(product: ProductData | undefined): INotesAndFilesItem[] {
    const ret: INotesAndFilesItem[] = [];
    if (product) {
      const main = NotesAndFilesHelper.#convertNotesAndFiles(product);
      if (main)
        ret.push({ descriptor: { type: "main", productId: product.id }, content: main })
      if (Product.isSunSail(product)) {
        for (let i = 0; i < Poles.getCount(product); i++) {
          if (SunSailLogic.hasSpecialConsole(product, i)) {
            const special = NotesAndFilesHelper.#convertNotesAndFiles(product.poles[i].specialConsole);
            if (special) {
              ret.push({ descriptor: { type: "specialConsole", poleIndex: i, productId: product.id }, content: special })
            }
          }
        }
      }
    }
    return ret;
  }

  static getNotesAndFilesFromShoppingCart(notesAndFiles: INotesAndFiles | undefined): INotesAndFilesItem[] {
    if (!notesAndFiles)
      return [];
    const content = NotesAndFilesHelper.#convertNotesAndFiles(notesAndFiles);
    if (!content)
      return [];
    return [{ descriptor: { type: "main", productId: "shoppingCart" }, content }]
  }

  static getNotesAndFilesFromProject(pp: IProjectPackage): INotesAndFilesItem[][] {
    const notesAndFiles: INotesAndFilesItem[][] = [];
    for (let i = 0; i < pp.sunSailAdapters.length; i++) {
      notesAndFiles[i] = [];
      const product = pp.sunSailAdapters[i].productData;
      if (product) {
        const naf = NotesAndFilesHelper.getNotesAndFilesFromProduct(product)
        if (naf)
          notesAndFiles[i].push(...naf);
      }
    }
    if (pp.shoppingCart?.notesAndFiles) {
      notesAndFiles[pp.sunSailAdapters.length] = [...pp.shoppingCart.notesAndFiles];
    }
    return notesAndFiles;
  }

  static async calcNotesAndFilesExtended(nafs: INotesAndFilesItem[], getFileInfo: FileInfoCallback): Promise<INotesAndFilesExtendedItem[]> {
    const allFiles: string[] = [];
    for (const naf of nafs) {
      const files = naf.content.files;
      if (files) {
        allFiles.push(...files);
      }
    }

    const fileInfos: INotesAndFilesExtendedItem["fileInfos"] = {}
    const promises = allFiles.map(async (f) => {
      let fi: IFileMiniInfo | undefined;
      try {
        fi = await getFileInfo(f);
      } catch (e) {
        console.log("Failed to get file info: ", e);
      }
      if (fi)
        fileInfos[f] = { name: fi.name, size: fi.size };
      else
        fileInfos[f] = { name: "File not found. ", size: 0 };
    });
    await Promise.all(promises);

    return nafs.map<INotesAndFilesExtendedItem>((n) => ({ ...n, fileInfos }));
  }
}